import { QueryOptions, WithRequired } from '@tanstack/react-query';

import {
  getCampaignMonthlyDeliverabilityMetrics,
  getCombinedMonthlyOptimizerMetrics,
  getCustomerFeedbacksForStore,
  getCustomerFeedbacksForUsecase,
  getFlowActionsForStoreKey,
  getFlowMessageExperimentAllocationConversionStats,
  getFlowMessageExperimentTargetMetrics,
  getFlowMessageOptimizerStats,
  getFlowMessagesForStoreKey,
  getFlowMonthlyOptimizerMetrics,
  getFlowsForStoreKey,
  getKlaviyoAccount,
  getKlaviyoAccountMetricsInSync,
  getLatestFlowExperimentPhaseForStoreKey,
  getOptimizerCampaignDeliverabilityExperimentMetrics,
  getPretesterInboxes,
  getSPOptimizerFlowMessageExperimentKlaviyoTemplates,
  getSPOptimizerFlowMessageExperimentMetricsForSPOptimizerStore,
  getSPOptimizerFlowMessageExperimentShardMetrics,
  getSPOptimizerFlowMessageExperimentVariantAggregatedMetrics,
  getSPOptimizerFlowMessageExperimentVariantsForExperiment,
  getSPOptimizerFlowMessageExperimentsForSPOptimizerStore,
  getSPOptimizerFlowMessageExperimentsForStore,
  getSPOptimizerStore,
  getSPOptimizerStoreAggregatedSummaryFlowMetrics,
  getSPOptimizerStoreExcludePhrases,
  getSPOptimizerStoreIncludePhrases,
  getSPOptimizerStores,
  getSavedVideoScripts,
  getShopifyShop,
  getStoreAggregatedMetrics,
  getStoreDailyMetrics,
  getUsecase,
  getUsecasesForStore,
  postShopifyCartProductImages,
} from './methods';

const makeQuery = <F extends (...a: [...Parameters<F>]) => Promise<Awaited<ReturnType<F>>>>(
  apiMethod: F,
  otherOptions: (
    ...args: [...Parameters<F>]
  ) => Omit<WithRequired<QueryOptions<Awaited<ReturnType<F>>>, 'queryKey'>, 'queryFn'>,
): ((...args: [...Parameters<F>]) => WithRequired<QueryOptions<Awaited<ReturnType<F>>>, 'queryFn' | 'queryKey'>) => {
  return (...args) => ({
    queryFn: () => apiMethod(...args),
    ...otherOptions(...args),
  });
};

export const queries = {
  usecase: makeQuery(getUsecase, (usecaseId: number) => ({
    queryKey: ['usecase', usecaseId],
  })),
  usecasesForStore: makeQuery(getUsecasesForStore, (storeKey: string | undefined) => ({
    queryKey: ['usecases', storeKey],
  })),
  spOptimizerStores: makeQuery(getSPOptimizerStores, () => ({
    queryKey: ['spOptimizerStores'],
  })),
  spOptimizerStore: makeQuery(getSPOptimizerStore, (spOptimizerStoreID: number) => ({
    queryKey: ['spOptimizerStore', spOptimizerStoreID],
  })),
  spOptimizerStoreExcludePhrases: makeQuery(getSPOptimizerStoreExcludePhrases, (spOptimizerStoreID: number) => ({
    queryKey: ['spOptimizerStoreExcludePhrases', spOptimizerStoreID],
  })),
  spOptimizerStoreIncludePhrases: makeQuery(getSPOptimizerStoreIncludePhrases, (spOptimizerStoreID: number) => ({
    queryKey: ['spOptimizerStoreIncludePhrases', spOptimizerStoreID],
  })),
  spOptimizerFlowMessageExperimentShardMetrics: makeQuery(getSPOptimizerFlowMessageExperimentShardMetrics, () => ({
    queryKey: ['spOptimizerFlowMessageExperimentShardMetrics'],
  })),
  storeAggregatedMetrics: makeQuery(
    getStoreAggregatedMetrics,
    (storeKey: string, days: number, useCaseId?: number) => ({
      queryKey: ['store', storeKey, 'usecase', useCaseId, 'aggregate-metrics', days],
    }),
  ),
  storeDailyMetrics: makeQuery(getStoreDailyMetrics, (storeKey: string, days: number, useCaseId?: number) => ({
    queryKey: ['store', storeKey, 'usecase', useCaseId, 'daily-metrics', days],
  })),
  storeFlowMonthlyOptimizerMetrics: makeQuery(
    getFlowMonthlyOptimizerMetrics,
    (spOptimizerStoreID: number | undefined) => ({
      queryKey: ['spOptimizerStore', spOptimizerStoreID, 'flow', 'monthly-metrics'],
    }),
  ),
  storeCampaignMonthlyDeliverabilityMetrics: makeQuery(
    getCampaignMonthlyDeliverabilityMetrics,
    (spOptimizerStoreId: number | undefined) => ({
      queryKey: ['spOptimizerStore', spOptimizerStoreId, 'campaign-deliverability', 'monthly-metrics'],
    }),
  ),
  storeCombinedMonthlyOptimizerMetrics: makeQuery(
    getCombinedMonthlyOptimizerMetrics,
    (spOptimizerStoreId: number | undefined, includeCampaigns?: boolean, includeFlows?: boolean) => ({
      queryKey: ['spOptimizerStore', spOptimizerStoreId, 'combined', 'monthly-metrics', includeCampaigns, includeFlows],
    }),
  ),
  flowMessageExperimentAllocationConversionStats: makeQuery(
    getFlowMessageExperimentAllocationConversionStats,
    (spOptimizerFlowMessageExperimentId: number) => ({
      queryKey: ['flow-message-experiment-allocation-conversion-stats', spOptimizerFlowMessageExperimentId],
    }),
  ),
  flowMessageExperimentTargetMetrics: makeQuery(
    getFlowMessageExperimentTargetMetrics,
    (spOptimizerFlowMessageExperimentId: number) => ({
      queryKey: ['flow-message-experiment-target-metrics', spOptimizerFlowMessageExperimentId],
    }),
  ),
  customerFeedbacksForUsecase: makeQuery(
    getCustomerFeedbacksForUsecase,
    (usecaseId: number, page = 1, pageSize = 10) => ({
      queryKey: ['usecase', 'user-feedback', usecaseId, page, pageSize],
    }),
  ),
  customerFeedbacksForStore: makeQuery(
    getCustomerFeedbacksForStore,
    (storeId: number | undefined, page = 1, pageSize = 25) => ({
      queryKey: ['store', 'user-feedback', storeId, page, pageSize],
    }),
  ),
  klaviyoAccount: makeQuery(getKlaviyoAccount, (klaviyoAccountId: number) => ({
    queryKey: ['klaviyo-account', klaviyoAccountId],
  })),
  shopifyShop: makeQuery(getShopifyShop, (shopifyShopId: number) => ({
    queryKey: ['shopify-shop', shopifyShopId],
  })),
  shopifyCartProductImages: makeQuery(
    postShopifyCartProductImages,
    (
      storeKey: string,
      shopifyCheckoutId: string | undefined | null,
      shopifyCheckoutToken: string | undefined | null,
      shopifyProductIds: string[],
    ) => ({
      queryKey: [
        'store',
        storeKey,
        'shopify-cart-product-images',
        shopifyCheckoutId,
        shopifyCheckoutToken,
        shopifyProductIds,
      ],
    }),
  ),
  savedVideoScripts: makeQuery(getSavedVideoScripts, (useCaseId: number) => ({
    queryKey: ['usecase', useCaseId, 'get-saved-video-scripts'],
  })),
  spOptimizerFlowMessageExperimentsForSPOptimizerStore: makeQuery(
    getSPOptimizerFlowMessageExperimentsForSPOptimizerStore,
    (spOptimizerStoreId: number | undefined) => ({
      queryKey: ['sp-optimizer-flow-message-experiments', 'for-sp-optimizer-store', spOptimizerStoreId],
    }),
  ),
  spOptimizerFlowMessageExperimentsForStore: makeQuery(
    getSPOptimizerFlowMessageExperimentsForStore,
    (storeKey: string | undefined) => ({
      queryKey: ['sp-optimizer-flow-message-experiments', 'for-store', storeKey],
    }),
  ),
  spOptimizerFlowMessageExperimentMetricsForSPOptimizerStore: makeQuery(
    getSPOptimizerFlowMessageExperimentMetricsForSPOptimizerStore,
    (spOptimizerStoreId: number) => ({
      queryKey: ['sp-optimizer-flow-message-experiment-metrics', 'for-sp-optimizer-store', spOptimizerStoreId],
    }),
  ),
  spOptimizerFlowMessageExperimentVariantAggregatedMetrics: makeQuery(
    getSPOptimizerFlowMessageExperimentVariantAggregatedMetrics,
    (spOptimizerFlowMessageExperimentVariantId: number, days: number, startDate?: string, endDate?: string) => ({
      queryKey: [
        'sp-optimizer-flow-message-experiment-variant',
        spOptimizerFlowMessageExperimentVariantId,
        'aggregate-metrics',
        days,
        'start-date',
        startDate,
        'end-date',
        endDate,
      ],
    }),
  ),
  spOptimizerStoreAggregatedSummaryFlowMetrics: makeQuery(
    getSPOptimizerStoreAggregatedSummaryFlowMetrics,
    (
      spOptimizerStoreId: number,
      days: number,
      flowId?: number,
      flowMessageId?: number,
      flowMessageVariantId?: number,
      startDate?: string,
      endDate?: string,
    ) => ({
      queryKey: [
        'sp-optimizer-store-aggregated-summary-flow-metrics',
        spOptimizerStoreId,
        'flow',
        flowId,
        'flow-message',
        flowMessageId,
        'flow-message-variant',
        flowMessageVariantId,
        'aggregate-metrics',
        days,
        'start-date',
        startDate,
        'end-date',
        endDate,
      ],
    }),
  ),
  flowsForStoreKey: makeQuery(getFlowsForStoreKey, (storeKey: string) => ({
    queryKey: ['flows-for-store-key', storeKey],
  })),
  flowMessageForStoreKey: makeQuery(getFlowMessagesForStoreKey, (storeKey: string) => ({
    queryKey: ['flow-messages-for-store-key', storeKey],
  })),
  flowActionsForStoreKey: makeQuery(getFlowActionsForStoreKey, (storeKey: string) => ({
    queryKey: ['flow-actions-for-store-key', storeKey],
  })),
  flowMessageOptimizerStats: makeQuery(getFlowMessageOptimizerStats, (storeKey: string) => ({
    queryKey: ['flow-messages-optimizer-stats-for-store-key', storeKey],
  })),
  latestFlowExperimentPhaseForStoreKey: makeQuery(getLatestFlowExperimentPhaseForStoreKey, (storeKey: string) => ({
    queryKey: ['latest-flow-experiment-phases-for-store-key', storeKey],
  })),
  spOptimizerFlowMessageExperimentVariantsForExperiment: makeQuery(
    getSPOptimizerFlowMessageExperimentVariantsForExperiment,
    (spOptimizerFlowMessageExperimentId: number, onlyActive?: boolean) => ({
      queryKey: [
        'sp-optimizer-flow-message-experiment-variants-for-experiment',
        spOptimizerFlowMessageExperimentId,
        'only-active',
        onlyActive,
      ],
    }),
  ),
  spOptimizerFlowMessageExperimentKlaviyoTemplates: makeQuery(
    getSPOptimizerFlowMessageExperimentKlaviyoTemplates,
    (spOptimizerFlowMessageExperimentId: number) => ({
      queryKey: ['sp-optimizer-flow-message-experiment-klaviyo-templates', spOptimizerFlowMessageExperimentId],
    }),
  ),
  pretesterInboxes: makeQuery(getPretesterInboxes, () => ({
    queryKey: ['pretester-inboxes'],
  })),
  klaviyoAccountMetricsInSync: makeQuery(getKlaviyoAccountMetricsInSync, (klaviyoAccountId: number) => ({
    queryKey: ['klaviyo-account', klaviyoAccountId, 'metrics-in-sync'],
  })),
  optimizerCampaignDeliverabilityExperimentMetrics: makeQuery(
    getOptimizerCampaignDeliverabilityExperimentMetrics,
    (spOptimizerStoreID: number, days: number, startDate?: string, endDate?: string) => ({
      queryKey: [
        'optimizer-campaign-deliverability-experiment-results',
        spOptimizerStoreID,
        'aggregate-metrics',
        days,
        'start-date',
        startDate,
        'end-date',
        endDate,
      ],
    }),
  ),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
} as const satisfies Record<string, (...args: any[]) => WithRequired<QueryOptions<any>, 'queryKey' | 'queryFn'>>;
